// src/components/app/Nav.tsx
import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBars,
  faTimes,
  faTruckFast,
  faUser,
  faChevronDown
} from "@fortawesome/free-solid-svg-icons";

import elPaketLogoIMG from "../../assets/images/logo-elpaket.png";
import "../../styles/app/Nav.css";

const Nav: React.FC = () => {
  const brandName = process.env.REACT_APP_BRAND_NAME;
  const [menuOpen, setMenuOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const leaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { customerData, logout } = useAuth();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        if (leaveTimeoutRef.current) {
          clearTimeout(leaveTimeoutRef.current);
          leaveTimeoutRef.current = null;
        }
        setUserDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      if (leaveTimeoutRef.current) {
        clearTimeout(leaveTimeoutRef.current);
      }
    };
  }, []);

  const handleMouseEnter = () => {
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }
    setUserDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
    }
    leaveTimeoutRef.current = setTimeout(() => {
      setUserDropdownOpen(false);
    }, 450);
  };
  
  const isHomePage = location.pathname === '/';
  const isDashboard = location.pathname.startsWith('/customer') || location.pathname.startsWith('/admin') || location.pathname.includes('dashboard');

  const toggleServicesDropdown = () => {
    setServicesDropdownOpen(!servicesDropdownOpen);
  };

  const closeAllMenus = () => {
    setMenuOpen(false);
    setServicesDropdownOpen(false);
  };

  const scrollToPickup = () => {
    const element = document.getElementById("pickup");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleAuthAction = () => {
    if (customerData) {
      navigate('/customer/dashboard');
    } else {
      navigate('/login');
    }
    setMenuOpen(false);
  };

  const handleLogoutAction = async () => {
    await logout();
    navigate('/');
    setMenuOpen(false);
  };

  const handleLogoClick = () => {
    if (location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  return (
    <nav className="nav">
      <div className="nav-container">
        <div className="nav-left">
          <img 
            src={elPaketLogoIMG} 
            alt="elPaket - Envíos de USA a Latinoamérica" 
            className="nav-logo-img" 
            onClick={handleLogoClick}
            style={{ height: '44px', objectFit: 'contain', cursor: 'pointer' }} // Ajusta la altura a tu gusto
          />
        </div>

        <div className={`nav-links ${menuOpen ? "active" : ""}`}>
          <a href="/pickup" onClick={() => setMenuOpen(false)}>
            Recogidas
          </a>
          <a href="/track" onClick={() => setMenuOpen(false)}>
            Rastreo
          </a>
          <a href="/calculator" onClick={() => setMenuOpen(false)}>
            Calculadora
          </a>
          <a href="/contacto" onClick={() => setMenuOpen(false)}>
            Soporte
          </a>

          {/* Link para acceso al panel/login (visible en móvil) */}
          {customerData ? (
            isDashboard ? (
              <div className="nav-mobile-user-section">
                <div className="nav-mobile-user-name">
                  Usuario: <strong>{customerData.FirstName} {customerData.LastName}</strong>
                </div>
                <button
                  onClick={handleLogoutAction}
                  className="nav-admin-link nav-logout-mobile"
                >
                  Cerrar Sesión
                </button>
              </div>
            ) : (
              <button
                onClick={handleAuthAction}
                className="nav-admin-link"
              >
                <FontAwesomeIcon icon={faUser} className="mr-2" /> Dashboard
              </button>
            )
          ) : (
            <button
              onClick={handleAuthAction}
              className="nav-admin-link"
            >
              <FontAwesomeIcon icon={faUser} className="mr-2" /> Iniciar Sesión
            </button>
          )}
        </div>

        <div className="nav-actions">
          {isHomePage && process.env.REACT_APP_SHOW_DISCOUNT === 'TRUE' && (
            <button className="nav-discount-button glow" onClick={scrollToPickup}>
              20% DESCUENTO
            </button>
          )}

          {/* Botón de login/dashboard para desktop */}
          {customerData ? (
            <div
              className="nav-user-dropdown-container"
              ref={dropdownRef}
            >
              <button
                onClick={() => {
                  setUserDropdownOpen(prev => !prev);
                }}
                className="nav-login-btn-text"
                title={`${customerData.FirstName} ${customerData.LastName}`}
              >
                <FontAwesomeIcon icon={faUser} />
                <span>{customerData.FirstName} {customerData.LastName}</span>
              </button>
              {userDropdownOpen && (
                <div className="nav-user-dropdown-menu">
                  <button
                    onClick={() => {
                      navigate('/customer/dashboard');
                      setUserDropdownOpen(false);
                    }}
                    className="nav-user-dropdown-item"
                  >
                    Mi Cuenta
                  </button>
                  <button
                    onClick={() => {
                      handleLogoutAction();
                      setUserDropdownOpen(false);
                    }}
                    className="nav-user-dropdown-item logout"
                  >
                    Cerrar Sesión
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              onClick={handleAuthAction}
              className="nav-login-btn-text"
            >
              <FontAwesomeIcon icon={faUser} />
              <span>Iniciar Sesión</span>
            </button>
          )}
        </div>

        <button
          className="nav-hamburger"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <FontAwesomeIcon icon={menuOpen ? faTimes : faBars} />
        </button>
      </div>
    </nav>
  );
};

export default Nav;